import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Flex,
  Heading,
  HStack,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import { FaBriefcase } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { useBusinessUnit } from "../../../context/BusinessUnitContext";
import { listConsultantAssignments } from "../../../api/consultantsApi";
import { EmptyState, KpiCard, KpiStrip, StatusBadge } from "../../../components/Portal/ui";
import { ASSIGNMENT_STATUS_COLORS } from "../../../constants/statuses";

const ConsultantAssignmentsTab = ({ consultantId }) => {
  const { t } = useTranslation("portal");
  const { selectedBusinessUnitId } = useBusinessUnit();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    if (!consultantId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await listConsultantAssignments(consultantId, {
        buId: selectedBusinessUnitId || undefined,
      });
      setData(result);
    } catch (e) {
      console.error("Failed to fetch assignments", e);
      setError(e?.response?.status || "unknown");
    } finally {
      setLoading(false);
    }
  }, [consultantId, selectedBusinessUnitId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <Flex align="center" justify="center" py="40px">
        <Spinner color="portal.primary" />
      </Flex>
    );
  }

  if (!data) {
    return (
      <EmptyState
        icon={<FaBriefcase />}
        title={
          error
            ? t("pilotConsultants.detail.assignments.loadError", {
                defaultValue: "Failed to load assignments. Please try again.",
              })
            : t("pilotConsultants.detail.assignments.noData", {
                defaultValue: "No assignment data available.",
              })
        }
      />
    );
  }

  const { totalYearsExperience, yearsInCompany, assignments } = data;

  const hasExperience =
    totalYearsExperience != null || yearsInCompany != null;

  return (
    <VStack align="stretch" spacing="14px">
      {/* Experience Header */}
      {hasExperience ? (
        <KpiStrip columns={{ base: 1, md: 2 }}>
          {totalYearsExperience != null && (
            <KpiCard
              label={t("pilotConsultants.detail.assignments.totalExperienceLabel", {
                defaultValue: "Total experience",
              })}
              value={t("pilotConsultants.detail.assignments.yearsValue", {
                defaultValue: "{{years}} years",
                years: totalYearsExperience,
              })}
            />
          )}
          {yearsInCompany != null && (
            <KpiCard
              label={t("pilotConsultants.detail.assignments.yearsInCompanyLabel", {
                defaultValue: "Within the company",
              })}
              value={t("pilotConsultants.detail.assignments.yearsValue", {
                defaultValue: "{{years}} years",
                years: yearsInCompany,
              })}
            />
          )}
        </KpiStrip>
      ) : (
        <Text fontSize="13px" color="portal.muted" sx={{ marginBottom: "0 !important" }}>
          {t("pilotConsultants.detail.assignments.noExperienceData", {
            defaultValue: "Experience data not set.",
          })}
        </Text>
      )}

      {/* Assignment Cards */}
      <Heading
        as="h3"
        fontSize="14px"
        letterSpacing="-0.02em"
        color="portal.text"
        sx={{ marginBottom: "0 !important" }}
      >
        {t("pilotConsultants.detail.assignments.historyTitle", {
          defaultValue: "Assignment History",
        })}
      </Heading>

      {assignments.length === 0 ? (
        <EmptyState
          icon={<FaBriefcase />}
          title={t("pilotConsultants.detail.assignments.noAssignments", {
            defaultValue: "No assignments found.",
          })}
        />
      ) : (
        <VStack align="stretch" spacing="8px" maxH="500px" overflowY="auto">
          {assignments.map((a) => (
            <Box
              key={a.matchId}
              borderWidth="1px"
              borderRadius="10px"
              p="12px"
              boxShadow="kpi"
              bg={a.isCurrent ? "green.50" : "portal.panel"}
              borderColor={a.isCurrent ? "green.300" : "portal.line"}
            >
              <Flex justify="space-between" align="flex-start" wrap="wrap" gap="8px">
                <Box flex={1} minW={0}>
                  <HStack spacing="8px" mb="2px">
                    <Text
                      fontSize="13px"
                      fontWeight="700"
                      color="portal.text"
                      isTruncated
                      sx={{ marginBottom: "0 !important" }}
                    >
                      {a.clientName || "-"}
                    </Text>
                    {a.isCurrent && (
                      <StatusBadge
                        label={t("pilotConsultants.detail.assignments.current", {
                          defaultValue: "Current",
                        })}
                        colorScheme="green"
                        flexShrink={0}
                      />
                    )}
                  </HStack>
                  <Text
                    fontSize="13px"
                    color="portal.text"
                    sx={{ marginBottom: "0 !important" }}
                  >
                    {a.needTitle || "-"}
                  </Text>
                  {a.description && (
                    <Text
                      fontSize="11px"
                      color="portal.muted"
                      mt="4px"
                      noOfLines={2}
                      sx={{ marginBottom: "0 !important" }}
                    >
                      {a.description}
                    </Text>
                  )}
                </Box>
                <VStack align="flex-end" spacing="6px" flexShrink={0}>
                  {a.assignmentStatus ? (
                    <StatusBadge
                      label={a.assignmentStatus}
                      colorScheme={ASSIGNMENT_STATUS_COLORS[a.assignmentStatus] || "gray"}
                    />
                  ) : (
                    <Text
                      fontSize="11px"
                      color="portal.muted"
                      sx={{ marginBottom: "0 !important" }}
                    >
                      -
                    </Text>
                  )}
                  <Text
                    fontSize="11px"
                    color="portal.muted"
                    textAlign="right"
                    sx={{ marginBottom: "0 !important" }}
                  >
                    {a.startDate
                      ? new Date(a.startDate).toLocaleDateString()
                      : "?"}
                    {" - "}
                    {a.endDate
                      ? new Date(a.endDate).toLocaleDateString()
                      : t("pilotConsultants.detail.assignments.ongoing", {
                          defaultValue: "Ongoing",
                        })}
                  </Text>
                </VStack>
              </Flex>
            </Box>
          ))}
        </VStack>
      )}
    </VStack>
  );
};

export default ConsultantAssignmentsTab;
